/*

? Chat and broadcast dates on the website, in the app's style (chat audit I12, 17 Oct 2026).

  A port of the Android app's DateUtils rules, so a date reads the same on every device:
    - a date is `dd MMM yyyy` (26 Aug 2026), month name in the page's locale;
    - a time is `hh:mm a` (05:30 PM) or `HH:mm` (13:00), following the browser's own 12 / 24-hour
      setting rather than always 12-hour;
    - the chat list stamp: "Now" (under a minute), the time (today), "Yesterday", the weekday (the
      last 7 days), else the date;
    - a thread's day header: TODAY, YESTERDAY, else the date in capitals.

  Used by the chat and broadcast screens only, for now. Words ("Now", "Yesterday", "Today") are
  passed in by the caller so its own translations apply. Client and server safe: Intl only.

*/

/** A Date from a Date, an ISO string or epoch millis; null when it can't be read. */
function toDate(value: Date | string | number | null | undefined): Date | null {
	if (value === null || value === undefined || value === "") return null;
	const date = value instanceof Date ? value : new Date(value);
	return isNaN(date.getTime()) ? null : date;
}

/** True when the browser (or the server's default locale) uses a 24-hour clock. */
function uses24Hour(): boolean {
	try {
		const cycle = new Intl.DateTimeFormat(undefined, { hour: "numeric" }).resolvedOptions().hourCycle;
		return cycle === "h23" || cycle === "h24";
	} catch {
		return false;
	}
}

const pad2 = (n: number): string => (n < 10 ? `0${n}` : String(n));

/** `26 Aug 2026`, the month in [locale]; "" when the date can't be read. */
export function formatChatDate(value: Date | string | number | null | undefined, locale?: string): string {
	const date = toDate(value);
	if (!date) return "";
	let month: string;
	try {
		month = new Intl.DateTimeFormat(locale || undefined, { month: "short" }).format(date);
	} catch {
		month = new Intl.DateTimeFormat("en", { month: "short" }).format(date);
	}
	return `${pad2(date.getDate())} ${month} ${date.getFullYear()}`;
}

/** `05:30 PM` or `13:00`, by the browser's 12 / 24-hour setting; "" when the date can't be read. */
export function formatChatTime(value: Date | string | number | null | undefined): string {
	const date = toDate(value);
	if (!date) return "";
	const hours = date.getHours();
	const minutes = pad2(date.getMinutes());
	if (uses24Hour()) return `${pad2(hours)}:${minutes}`;
	const hour12 = hours % 12 === 0 ? 12 : hours % 12;
	return `${pad2(hour12)}:${minutes} ${hours < 12 ? "AM" : "PM"}`;
}

/** Same calendar day in local time. */
const sameDay = (a: Date, b: Date): boolean =>
	a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * A chat header's last-seen line (18 Oct 2026, the app's formatLastSeen): "last seen today at 05:30 PM",
 * "last seen yesterday at 05:30 PM", then "last seen 26 Aug 2026". [words] carries the caller's
 * translations; "" when the date can't be read.
 */
export function formatLastSeen(
	value: Date | string | number | null | undefined,
	locale: string | undefined,
	words: { today: (time: string) => string; yesterday: (time: string) => string; date: (date: string) => string },
): string {
	const date = toDate(value);
	if (!date) return "";
	const now = new Date();
	const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
	if (sameDay(date, now)) return words.today(formatChatTime(date));
	if (sameDay(date, yesterday)) return words.yesterday(formatChatTime(date));
	return words.date(formatChatDate(date, locale));
}

/**
 * The chat list stamp: "Now", the time today, "Yesterday", the weekday within 7 days, else the
 * date. [words] carries the caller's translations of "Now" and "Yesterday".
 */
export function formatChatListStamp(
	value: Date | string | number | null | undefined,
	locale?: string,
	words: { now: string; yesterday: string } = { now: "Now", yesterday: "Yesterday" },
): string {
	const date = toDate(value);
	if (!date) return "";
	const now = new Date();
	if (now.getTime() - date.getTime() < 60 * 1000) return words.now;
	if (sameDay(date, now)) return formatChatTime(date);
	const yesterday = new Date(now);
	yesterday.setDate(now.getDate() - 1);
	if (sameDay(date, yesterday)) return words.yesterday;
	const weekAgo = new Date(now);
	weekAgo.setDate(now.getDate() - 7);
	weekAgo.setHours(23, 59, 59, 999);
	if (date.getTime() > weekAgo.getTime()) {
		try {
			return new Intl.DateTimeFormat(locale || undefined, { weekday: "long" }).format(date);
		} catch {
			return new Intl.DateTimeFormat("en", { weekday: "long" }).format(date);
		}
	}
	return formatChatDate(date, locale);
}

/**
 * A thread's day header: TODAY, YESTERDAY, else the date, in capitals as the app draws it.
 * [words] carries the caller's translations of "Today" and "Yesterday".
 */
export function formatChatDayHeader(
	value: Date | string | number | null | undefined,
	locale?: string,
	words: { today: string; yesterday: string } = { today: "Today", yesterday: "Yesterday" },
): string {
	const date = toDate(value);
	if (!date) return "";
	const now = new Date();
	if (sameDay(date, now)) return words.today.toUpperCase();
	const yesterday = new Date(now);
	yesterday.setDate(now.getDate() - 1);
	if (sameDay(date, yesterday)) return words.yesterday.toUpperCase();
	return formatChatDate(date, locale).toUpperCase();
}
